import { findRegionByLocale } from '@/common/region-locale'
import { requestGetSupport, requestV2 } from './request'
import { SEOData } from './seo.service'
import qs from 'qs'
import { ContactUsSectionData } from '@/components/contact-us-section'

export interface RobotInfo {
  processor?: string
  hasAfterSaleSys?: number
  sysUrl?: string
  responseTime?: string
  phone?: string
  email?: string
  workTimes?: string[]
  workDays?: string[]
  robotCategory?: string
  robotSn?: string
  robotMac?: string
}

export interface FaultAttachmentPayloadItem {
  attach_url: string
  attach_type: string
}

export interface SubmitIssueFeedbackPayload {
  mac: string
  sn: string
  logPath: string
  logUrl: string
  errorType: string
  errorDesc: string
  faultTime: number
  timezone: string
  attachUrl: FaultAttachmentPayloadItem[]
  contactCountryCrmId: string
  contactInfo: string
  email: string
  emailVerificationCode: string
  phone: string
}

export interface UploadSignatureItem {
  method?: string
  header?: Record<string, string>
  isp?: string
  fileName: string
  uploadUrl: string
  getUrl: string
}

export interface UploadedAttachment {
  url: string
  name: string
  ext: string
}

export interface ApiResponse<T> {
  code: number
  message: string
  data: T
}

/**
 * 工单状态枚举值：
 * 1-待确认 2-处理中 3-已升级 4-已完成 5-已复核 6-已取消
 */
export type OrderStatusCode = '1' | '2' | '3' | '4' | '5' | '6'

export interface OrderProgressData {
  order_number: string
  order_status: OrderStatusCode
}

export interface PhoneAreaCodeOption {
  code: string
  label: string
}

// 页面数据（Banner、联系我们等）
export interface FaultReportingPageData {
  banner: {
    title: string
    description: string
    bg_image: {
      url: string
    }
  }
  contect_us: ContactUsSectionData
  seoData?: SEOData | null
}

export async function getRobotInfoService(
  params: { pidOrMac: string },
  locale: string
): Promise<ApiResponse<RobotInfo>> {
  const region = findRegionByLocale(locale)
  return requestGetSupport<ApiResponse<RobotInfo>>('/community-center-service/api/after_sale/v1/get', {
    ...params,
    region,
  })
}

getRobotInfoService.swrkey = (locale: string, pidOrMac: string) =>
  `/${locale}/api/front/v2/community-center-service/api/after_sale/v1/get/${pidOrMac}`

export async function sendCheckEmailService(
  params: { email: string },
  locale: string
): Promise<ApiResponse<null>> {
  const region = findRegionByLocale(locale)
  return requestGetSupport<ApiResponse<null>>(
    '/community-center-service/api/after_sale/v1/send_check_email',
    {
      ...params,
      region,
    }
  )
}

export async function getIssueInfoService(
  params: { uuid: string },
  locale: string
): Promise<ApiResponse<string>> {
  const region = findRegionByLocale(locale)
  return requestGetSupport<ApiResponse<string>>(
    '/community-center-service/api/after_sale/v1/get_cache_after_sale_order_url',
    {
      ...params,
      region,
    }
  )
}

export async function submitIssueFeedbackService(
  payload: SubmitIssueFeedbackPayload,
  locale: string
): Promise<ApiResponse<null>> {
  const region = findRegionByLocale(locale)
  return requestGetSupport<ApiResponse<null>>('/community-center-service/api/after_sale/v1/commit', {
    ...payload,
    region,
  })
}

async function getUploadSignatureService(
  fileNames: string[],
  locale: string
): Promise<ApiResponse<UploadSignatureItem[]>> {
  const region = findRegionByLocale(locale)
  return requestGetSupport<ApiResponse<UploadSignatureItem[]>>(
    '/community-center-service/api/after_sale/v1/get_oss_upload_paths',
    {
      fileNames,
      region,
    }
  )
}

export async function uploadIssueAttachmentService(
  file: File,
  locale: string,
  options?: {
    timeoutSeconds?: number
    onProgress?: (progress: number) => void
  }
): Promise<UploadedAttachment> {
  const ext = file.name.split('.').pop()?.toLowerCase() || ''
  const signed = await getUploadSignatureService([file.name], locale)
  if (signed.code !== 200 || !signed.data?.length) {
    throw new Error(signed.message || 'Get upload signature failed')
  }

  const signature = signed.data[0]
  let uploadUrl = signature.uploadUrl || ''
  if (uploadUrl.startsWith('http://')) {
    uploadUrl = uploadUrl.replace('http://', 'https://')
  }

  let previewUrl = signature.getUrl || ''
  if (previewUrl.startsWith('http://')) {
    previewUrl = previewUrl.replace('http://', 'https://')
  }

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.timeout = (options?.timeoutSeconds || 60) * 1000

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable) {
        return
      }
      const progress = Number(((event.loaded / event.total) * 100).toFixed(2))
      options?.onProgress?.(progress)
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
        return
      }
      reject(new Error(`Upload failed with status ${xhr.status}`))
    })

    xhr.addEventListener('error', () => reject(new Error('Upload network error')))
    xhr.addEventListener('timeout', () => reject(new Error('Upload timeout')))
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')))

    xhr.open(signature.method || 'PUT', uploadUrl, true)
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    Object.entries(signature.header || {}).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })
    xhr.send(file)
  })

  return {
    url: previewUrl,
    name: file.name,
    ext,
  }
}

export async function queryServiceProgress(
  orderNumber: string
): Promise<ApiResponse<OrderProgressData>> {
  return requestGetSupport<ApiResponse<OrderProgressData>>(
    '/company-center-service/api/after_sale/v1/get_after_sale_order_info',
    { order_number: orderNumber }
  )
}

queryServiceProgress.swrkey = (orderNumber: string) =>
  `/api/after_sale/v1/get_after_sale_order_info/${orderNumber}`

// 获取页面数据
const query = qs.stringify(
  {
    populate: {
      contect_us: {
        populate: {
          rows: {
            populate: {
              items: {
                populate: {
                  iconUrl: {
                    fields: ['url'],
                  },
                  iconDetailUrl: {
                    fields: ['url'],
                  },
                },
              },
            },
          },
        },
      },
      banner: {
        fields: ['title', 'description'],
        populate: {
          bg_image: {
            fields: ['url'],
          },
        },
      },
    },
  },
  { encodeValuesOnly: true }
)
export async function getFaultReportingPageData(
  locale: string
): Promise<FaultReportingPageData> {
  const region = findRegionByLocale(locale)
  const res = await requestV2('/common/data', {
    url: `/api/fault-reporting?status=published&${query}`,
    region,
  })
  return res?.data
}

getFaultReportingPageData.swrkey = (locale: string) =>
  `/${locale}/api/front/v2/common/data/fault-reporting`

/**
 * 获取电话区号列表（Mock 数据）
 * 后续接入真实接口时可保持返回结构不变
 */
export async function getPhoneAreaCodeOptionsMock(): Promise<PhoneAreaCodeOption[]> {
  return Promise.resolve([
    { code: '+86', label: '+86' },
    { code: '+852', label: '+852' },
    { code: '+81', label: '+81' },
    { code: '+82', label: '+82' },
    { code: '+49', label: '+49' },
    { code: '+33', label: '+33' },
    { code: '+34', label: '+34' },
    { code: '+39', label: '+39' },
    { code: '+44', label: '+44' },
    { code: '+1', label: '+1' },
  ])
}

getPhoneAreaCodeOptionsMock.swrkey = (locale: string) =>
  `/${locale}/api/front/v2/fault-reporting/mock/phone-area-codes`
